import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, X, Save, Sparkles, Building2, Type, Palette } from "lucide-react";
import { useFileUpload } from "@/hooks/notifications/useFileUpload";
import { useAdminCustomization } from "@/hooks/useAdminCustomization";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const FONT_OPTIONS = [
  { value: 'inter', label: 'Inter (Modern Sans)', family: 'Inter, sans-serif', url: 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap' },
  { value: 'roboto', label: 'Roboto (Clean Sans)', family: 'Roboto, sans-serif', url: 'https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap' },
  { value: 'poppins', label: 'Poppins (Geometric)', family: 'Poppins, sans-serif', url: 'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap' },
  { value: 'montserrat', label: 'Montserrat (Bold)', family: 'Montserrat, sans-serif', url: 'https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&display=swap' },
  { value: 'lato', label: 'Lato (Professional)', family: 'Lato, sans-serif', url: 'https://fonts.googleapis.com/css2?family=Lato:wght@300;400;700&display=swap' },
  { value: 'opensans', label: 'Open Sans (Friendly)', family: '"Open Sans", sans-serif', url: 'https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;500;600;700&display=swap' },
  { value: 'playfair', label: 'Playfair (Elegant Serif)', family: '"Playfair Display", serif', url: 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&display=swap' },
  { value: 'merriweather', label: 'Merriweather (Classic Serif)', family: 'Merriweather, serif', url: 'https://fonts.googleapis.com/css2?family=Merriweather:wght@300;400;700&display=swap' },
];

const COLOR_PRESETS = [
  { name: 'Blue', value: '221 83% 53%', preview: 'hsl(221, 83%, 53%)' },
  { name: 'Purple', value: '262 83% 58%', preview: 'hsl(262, 83%, 58%)' },
  { name: 'Green', value: '142 76% 36%', preview: 'hsl(142, 76%, 36%)' },
  { name: 'Orange', value: '25 95% 53%', preview: 'hsl(25, 95%, 53%)' },
  { name: 'Red', value: '0 84% 60%', preview: 'hsl(0, 84%, 60%)' },
  { name: 'Teal', value: '173 58% 39%', preview: 'hsl(173, 58%, 39%)' },
  { name: 'Pink', value: '330 81% 60%', preview: 'hsl(330, 81%, 60%)' },
  { name: 'Indigo', value: '239 84% 67%', preview: 'hsl(239, 84%, 67%)' },
  { name: 'Cyan', value: '189 94% 43%', preview: 'hsl(189, 94%, 43%)' },
  { name: 'Dark Blue', value: '222 47% 11%', preview: 'hsl(222, 47%, 11%)' },
];

export const AdminBrandingSettings = () => {
  const { customization, loading, saveCustomization } = useAdminCustomization();
  const { uploadFile } = useFileUpload();
  const [appName, setAppName] = useState('');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [fontFamily, setFontFamily] = useState('inter');
  const [primaryColor, setPrimaryColor] = useState('221 83% 53%');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (customization) {
      setAppName(customization.app_name || '');
      setLogoUrl(customization.logo_url);
      setFontFamily(customization.font_family || 'inter');
      setPrimaryColor(customization.primary_color || '221 83% 53%');
    }
  }, [customization]);

  useEffect(() => {
    // Load selected font
    const selectedFont = FONT_OPTIONS.find(f => f.value === fontFamily);
    if (selectedFont) {
      const existingLink = document.querySelector(`link[data-font="${fontFamily}"]`);
      if (!existingLink) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = selectedFont.url;
        link.setAttribute('data-font', fontFamily);
        document.head.appendChild(link);
      }
      // Apply font to root and body immediately for preview
      document.documentElement.style.setProperty('--font-family', selectedFont.family);
      document.body.style.fontFamily = selectedFont.family;
    }
  }, [fontFamily]);

  useEffect(() => {
    // Apply primary color immediately for preview
    document.documentElement.style.setProperty('--primary', primaryColor);
  }, [primaryColor]);

  const handleLogoUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      return;
    }

    setUploading(true);
    try {
      const publicUrl = await uploadFile(file, 'avatars', `admin-logo-${Date.now()}`);
      if (publicUrl) {
        setLogoUrl(publicUrl);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!appName.trim()) {
      return;
    }

    setSaving(true);
    try {
      await saveCustomization(appName, logoUrl, fontFamily, primaryColor);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveLogo = () => {
    setLogoUrl(null);
  };

  if (loading) {
    return (
      <Card className="border-0 shadow-none">
        <CardContent className="p-6">
          <div className="text-center text-muted-foreground">Loading...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-none p-5">
      <CardHeader className="px-0">
        <div className="flex items-center space-x-3 mb-2">
          <div className="p-2 bg-gradient-to-br from-primary/10 to-blue-500/10 rounded-xl">
            <Building2 className="h-5 w-5 text-primary" />
          </div>
          <CardTitle className="text-2xl">Brand Customization</CardTitle>
        </div>
        <CardDescription className="text-base">
          Customize the application name and logo for you and all users you create
        </CardDescription>
      </CardHeader>

      <Separator className="my-6" />

      <CardContent className="px-0 space-y-8">
        {/* Logo Upload */}
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-semibold text-foreground">Custom Logo</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            Upload a logo that will be displayed throughout the application for you and your users
          </p>
          
          <div className="flex items-start space-x-6">
            <div className="flex flex-col items-center space-y-3">
              {logoUrl ? (
                <div className="relative">
                  <div className="w-32 h-32 border-2 border-border rounded-xl overflow-hidden bg-card shadow-lg">
                    <img
                      src={logoUrl}
                      alt="Custom logo"
                      className="w-full h-full object-contain p-2"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                  </div>
                  <Button
                    size="sm"
                    variant="destructive"
                    className="absolute -top-2 -right-2 h-7 w-7 rounded-full p-0 shadow-lg"
                    onClick={handleRemoveLogo}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="w-32 h-32 border-2 border-dashed border-muted-foreground/30 rounded-xl flex items-center justify-center bg-muted/30">
                  <Upload className="h-8 w-8 text-muted-foreground/50" />
                </div>
              )}
              
              <div className="w-full">
                <Input
                  id="logo-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      handleLogoUpload(file);
                    }
                  }}
                  disabled={uploading}
                />
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => document.getElementById('logo-upload')?.click()}
                  disabled={uploading}
                >
                  {uploading ? (
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      Uploading...
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Upload className="h-4 w-4" />
                      {logoUrl ? 'Replace Logo' : 'Upload Logo'}
                    </div>
                  )}
                </Button>
              </div>
            </div>

            <div className="flex-1 space-y-2">
              <div className="rounded-lg border border-muted bg-muted/30 p-4">
                <h4 className="text-sm font-semibold mb-2">Logo Guidelines:</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Recommended size: 200x200 pixels or larger</li>
                  <li>• Accepted formats: PNG, JPG, WEBP</li>
                  <li>• Transparent backgrounds work best</li>
                  <li>• Square or circular logos are ideal</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <Separator />

        {/* Application Name */}
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-semibold text-foreground">Application Name</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            Set a custom name that will be displayed in the app header and navigation
          </p>
          
          <div className="max-w-md">
            <Label htmlFor="app-name" className="text-sm font-medium mb-2 block">
              Application Name
            </Label>
            <Input
              id="app-name"
              type="text"
              placeholder="Enter application name (e.g., Your Company Security)"
              value={appName}
              onChange={(e) => setAppName(e.target.value)}
              className="transition-all duration-200 focus:ring-2 focus:ring-primary/20"
            />
            <p className="text-xs text-muted-foreground mt-2">
              This name will be visible to all users you create
            </p>
          </div>
        </div>

        <Separator />

        {/* Font Selection */}
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Type className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-semibold text-foreground">Font Family</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            Choose a font that matches your brand identity
          </p>
          
          <div className="max-w-md space-y-4">
            <div>
              <Label htmlFor="font-family" className="text-sm font-medium mb-2 block">
                Select Font
              </Label>
              <Select value={fontFamily} onValueChange={setFontFamily}>
                <SelectTrigger id="font-family">
                  <SelectValue placeholder="Select a font" />
                </SelectTrigger>
                <SelectContent>
                  {FONT_OPTIONS.map((font) => (
                    <SelectItem key={font.value} value={font.value} style={{ fontFamily: font.family }}>
                      {font.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-2">
                The selected font will be applied throughout the application
              </p>
            </div>

            {/* Font Preview */}
            <div className="rounded-lg border bg-card p-4">
              <p className="text-xs text-muted-foreground mb-2">Font Preview:</p>
              <div style={{ fontFamily: FONT_OPTIONS.find(f => f.value === fontFamily)?.family }}>
                <p className="text-2xl font-bold mb-2">{appName || 'Application Name'}</p>
                <p className="text-base mb-1">The quick brown fox jumps over the lazy dog</p>
                <p className="text-sm text-muted-foreground">0123456789 !@#$%^&*()</p>
              </div>
            </div>
          </div>
        </div>

        <Separator />

        {/* Color Selection */}
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Palette className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-semibold text-foreground">Primary Color</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            Choose a primary color that matches your brand identity
          </p>
          
          <div className="space-y-4">
            <div className="grid grid-cols-5 gap-3">
              {COLOR_PRESETS.map((color) => (
                <button
                  key={color.name}
                  onClick={() => setPrimaryColor(color.value)}
                  className={`relative h-16 rounded-lg border-2 transition-all hover:scale-105 ${
                    primaryColor === color.value ? 'border-foreground ring-2 ring-foreground/20' : 'border-border'
                  }`}
                  style={{ backgroundColor: color.preview }}
                  title={color.name}
                >
                  {primaryColor === color.value && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-lg">
                        <div className="w-3 h-3 bg-foreground rounded-full" />
                      </div>
                    </div>
                  )}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              Selected: {COLOR_PRESETS.find(c => c.value === primaryColor)?.name || 'Custom'}
            </p>

            {/* Color Preview */}
            <div className="rounded-lg border bg-card p-4">
              <p className="text-xs text-muted-foreground mb-3">Color Preview:</p>
              <div className="space-y-2">
                <Button style={{ backgroundColor: `hsl(${primaryColor})`, color: 'white' }}>
                  Primary Button
                </Button>
                <Button variant="outline" style={{ borderColor: `hsl(${primaryColor})`, color: `hsl(${primaryColor})` }}>
                  Outline Button
                </Button>
              </div>
            </div>
          </div>
        </div>

        <Separator />

        {/* Preview */}
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-semibold text-foreground">Preview</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            This is how your branding will appear in the header
          </p>
          
          <div className="rounded-lg border bg-card p-4 shadow-sm">
            <div className="flex items-center space-x-3">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt="Logo preview"
                  className="w-10 h-10 object-contain rounded-lg"
                />
              ) : (
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Building2 className="h-6 w-6 text-primary" />
                </div>
              )}
              <span className="text-xl font-semibold">
                {appName || 'Application Name'}
              </span>
            </div>
          </div>
        </div>

        <Separator />

        {/* Save Button */}
        <div className="flex justify-end pt-4">
          <Button 
            onClick={handleSave} 
            disabled={saving || !appName.trim()}
            size="lg"
            className="px-8 shadow-lg hover:shadow-xl transition-all duration-300"
          >
            {saving ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Customization
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};